import { XCircleIcon } from '@heroicons/react/24/solid';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import isEmpty from 'lodash.isempty';
import PropTypes from 'prop-types';
import { useState } from 'react';
import Alert from '../../../components/Alert';
import ChallengeQuestions from '../../../components/ChallengeQuestions';
import { useApplicationStore } from './applicationStore';

const VerifyExistingClient = ({
	isExistingConfirmed,
	setExistingConfirmed,
	onStubbedAccountConfirmed,
	isProfileUpdate = false,
}) => {
	const { getClient, challengeResponses, setChallengeResponses, setStatus } =
		useApplicationStore();
	const [last4SSN, setLast4SSN] = useState('');
	const [ssnChecked, setSsnChecked] = useState(false);
	const [challengeChecked, setChallengeChecked] = useState(false);
	const [last4DOB, setLast4DOB] = useState('');
	const [dobChecked, setDobChecked] = useState(false);

	const clientQuestionsQuery = useQuery({
		queryKey: ['clientQuestions', getClient().id],
		queryFn: async () => {
			const { data } = await axios.get(
				`/requests/client-security-questions/${getClient().id}`,
			);
			return data;
		},
		enabled: Boolean(getClient()?.id),
	});
	const questionsQuery = useQuery({
		queryKey: ['questions'],
		queryFn: async () => {
			const { data } = await axios.get('/clients/security-questions');
			return data;
		},
	});
	const clientAnswersQuery = useQuery({
		queryKey: ['answers', challengeResponses],
		queryFn: async () => {
			const { data } = await axios.post(
				`/requests/client-security-questions/${getClient().id}`,
				{
					answers: challengeResponses,
				},
			);
			return data;
		},
		enabled: false,
	});

	const handleCheckSSN = (event) => {
		event.preventDefault();
		setChallengeChecked(false);
		setSsnChecked(true);
		setExistingConfirmed(last4SSN === getClient().last_4_ssn);
	};

	const handleCheckAnswers = (event) => {
		event.preventDefault();
		setSsnChecked(false);
		setChallengeChecked(true);
		clientAnswersQuery.refetch().then(({ data }) => {
			setExistingConfirmed(!data.incorrect_answers);
		});
	};

	const handleCheckDOB = (event) => {
		event.preventDefault();
		setDobChecked(true);
		setExistingConfirmed(last4DOB === getClient().birth_dttm?.slice(0, 4));
		onStubbedAccountConfirmed();
	};

	const handleAccountSecured = (responses) => {
		setChallengeResponses(responses);
		setStatus(undefined);
	};

	const questionsNotSet =
		clientQuestionsQuery.isSuccess && clientQuestionsQuery.data.length === 0;
	const pinNotSet = isEmpty(getClient().last_4_ssn);

	if (clientQuestionsQuery.isLoading || questionsQuery.isLoading) {
		return null;
	}

	return (
		<>
			<div className="mb-4">
				<Alert
					type="warning"
					message="NOTE: Allow the client to fill in these values!"
				/>
			</div>
			{isProfileUpdate && questionsNotSet && pinNotSet ? (
				<>
					<p className="mb-4">
						<em>Confirm the birth year of the client</em>
					</p>
					<div className="form-control">
						<label className="label" htmlFor="last4dob">
							Year of Birth (YYYY)
						</label>
						<input
							type="text"
							name="last4dob"
							id="last4dob"
							autoComplete="off"
							className="input input-bordered w-36"
							onChange={({ target }) => setLast4DOB(target.value)}
							value={last4DOB}
							maxLength={4}
						/>
					</div>
					{dobChecked &&
						!isExistingConfirmed &&
						clientAnswersQuery.isFetched && (
							<div className="label text-error">
								<span>
									<XCircleIcon className="inline-block w-4 h-4 text-error" />{' '}
									Does not match!
								</span>
							</div>
						)}
					<button
						className="mt-4 btn-block btn btn-primary"
						onClick={handleCheckDOB}
					>
						Verify Year
					</button>
				</>
			) : (
				<>
					<p className="mb-4">
						<em>
							Verify account ownership by successfully answering 2 of 3
							challenge questions or the correct 4 digit verification number
						</em>
					</p>
					<div className="flex w-full gap-4 mt-6 mb-8">
						<div className="flex flex-col flex-1 gap-4 px-6 py-4 border border-gray-200 rounded-lg">
							{questionsNotSet ? (
								<Alert
									type="warning"
									message="No challenge questions set for this client."
								/>
							) : (
								<>
									<ChallengeQuestions
										questions={questionsQuery?.data}
										onResponsesChange={handleAccountSecured}
										required={false}
										clientQuestions={clientQuestionsQuery?.data}
										responsesOnly
									/>
									{challengeChecked &&
										!isExistingConfirmed &&
										clientAnswersQuery.isFetched && (
											<div className="label text-error">
												<span>
													<XCircleIcon className="inline-block w-4 h-4 text-error" />{' '}
													Does not match!
												</span>
											</div>
										)}
									<button
										className="btn-block btn btn-primary"
										onClick={handleCheckAnswers}
									>
										Verify Answers
									</button>
								</>
							)}
						</div>
						<div className="divider divider-horizontal">OR</div>
						<div className="flex flex-col flex-1 gap-4 px-6 py-4 border border-gray-200 rounded-lg">
							{pinNotSet ? (
								<Alert
									type="warning"
									message="No account verification number on record for this client."
								/>
							) : (
								<>
									<div className="form-control">
										<label className="label" htmlFor="ssn">
											4 digit verification number
										</label>
										<input
											type="password"
											name="ssn"
											id="ssn"
											autoComplete="off"
											className="input input-bordered"
											onChange={({ target }) => setLast4SSN(target.value)}
											value={last4SSN}
											maxLength={4}
										/>
										{!isExistingConfirmed && ssnChecked && (
											<div className="label text-error">
												<span>
													<XCircleIcon className="inline-block w-4 h-4 text-error" />{' '}
													Does not match!
												</span>
											</div>
										)}
									</div>
									<button
										className="btn btn-primary btn-block"
										onClick={handleCheckSSN}
										disabled={last4SSN.length < 4}
									>
										Verify
									</button>
								</>
							)}
						</div>
					</div>
				</>
			)}
		</>
	);
};

VerifyExistingClient.propTypes = {
	isExistingConfirmed: PropTypes.bool.isRequired,
	setExistingConfirmed: PropTypes.func.isRequired,
	onStubbedAccountConfirmed: PropTypes.func,
	isProfileUpdate: PropTypes.bool,
};

export default VerifyExistingClient;
